import React, { useState, useEffect } from 'react';
import { Drawer } from '../common/Drawer';
import { CustomerSidebar } from '../common/CustomerSidebar';
import { User, Activity, Package, Clock, DollarSign, FileText } from 'lucide-react';
import type { CustomerDocType } from '../../db/schemas';
import { useBranches } from '../../db/hooks/useLookupData';
import { usePOSStore } from '../../stores';
import type { VerticalTab } from '../common/VerticalTabs';

import { CustomerTabSummary } from './tabs/CustomerTabSummary';
import { CustomerTabPackages } from './tabs/CustomerTabPackages';
import { CustomerTabAttentions } from './tabs/CustomerTabAttentions';
import { CustomerTabTransactions } from './tabs/CustomerTabTransactions';
import { CustomerTabData } from './tabs/CustomerTabData';
import { CustomerTabNotes } from './tabs/CustomerTabNotes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customer?: CustomerDocType | null;
  initialTab?: string;
}

export function CustomerProfileDrawer({ isOpen, onClose, customer, initialTab = 'summary' }: Props) {
  const targetCustomer = customer || null;
  const [activeTab, setActiveTab] = useState(initialTab);
  const { branches } = useBranches();
  const openPOS = usePOSStore((s) => s.open);

  useEffect(() => {
    if (isOpen) {
      const normalizedInitial = initialTab === 'resumen' ? 'summary' : initialTab === 'datos' ? 'details' : initialTab;
      setActiveTab(normalizedInitial);
    }
  }, [isOpen, initialTab]);

  if (!isOpen || !targetCustomer) return null;

  const tabs: VerticalTab[] = [
    { id: 'summary', label: 'Resumen', icon: Activity },
    { id: 'packages', label: 'Paquetes', icon: Package },
    { id: 'attentions', label: 'Atenciones', icon: Clock },
    { id: 'transactions', label: 'Transacciones', icon: DollarSign },
    { id: 'details', label: 'Datos Personales', icon: User },
    { id: 'notes', label: 'Formularios y Notas', icon: FileText },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'summary':
      case 'resumen':
        return <CustomerTabSummary customer={targetCustomer} />;
      case 'packages':
        return <CustomerTabPackages customerId={targetCustomer?.id || ''} customerName={targetCustomer?.name || ''} />;
      case 'attentions':
        return <CustomerTabAttentions customerId={targetCustomer?.id || ''} />;
      case 'transactions':
        return <CustomerTabTransactions customerId={targetCustomer?.id || ''} />;
      case 'details':
      case 'datos':
        return <CustomerTabData customer={targetCustomer} />;
      case 'notes':
        return <CustomerTabNotes customer={targetCustomer} />;
      default:
        return null;
    }
  };

  const branch = branches.find(s => s.id === targetCustomer?.branch_id);

  const sidebar = (
    <CustomerSidebar
      variant="profile"
      customer={targetCustomer}
      branchName={branch ? branch.name : 'Sede Global'}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabSectionAfter={['transactions']}
      tabSectionHeaders={{'details': 'CONFIGURACIÓN', 'datos': 'CONFIGURACIÓN'}}
      onNewSale={() => {
        onClose();
        openPOS(targetCustomer);
      }}
    />
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Perfil del Cliente"
      subtitle="Visualiza y gestiona la información completa del cliente"
      icon={User}
      maxWidth="4xl"
      sidebar={sidebar}
    >
      {/* Right Column: Tab Content */}
      <div className="bg-surface-secondary backdrop-blur-md border border-border rounded-2xl p-5 shadow-xs flex-1 min-h-0">
        {renderContent()}
      </div>
    </Drawer>
  );
}
